import React from "react";
import Button from "src/design-system/components/Button/Button";
import {
  ClinicalIntroContentProps,
  ContentContainer,
  ContentInnerScrollContainer,
  ContentStickyElement,
  ContentText,
  ContentTitle,
  ScrollableContentContainer,
} from "./commonStyles";
import { trackBuyflowEvent } from "src/utils/api/tracker";
import BodyWeightLoss from "../images/bodyWeightLoss.svg";
import WeightGraphMed from "../images/WeightGraphMed.webp";
import DoctorsRecommend from "../images/doctorsRecommend.webp";
import {
  fontCaption,
  fontP2Regular,
  fontTitle,
} from "src/design-system/styles/fonts";
import styled from "@emotion/styled";
import {
  breakpointDesktopOnly,
  breakpointWideDesktopOnly,
} from "src/design-system/styles/breakpoints";
import { compassColors } from "src/utils/styling";
import i18n from "@i18n";
import { css } from "@emotion/react";
import { Text } from "src/components/core/Text";

export const ContentTextLarge = styled.p`
  ${fontP2Regular}
  @media ${breakpointDesktopOnly} {
    margin: 20px auto 30px;
    padding-left: min(60px, 20%);
    padding-right: min(60px, 20%);
  }
`;

const CustomContainer = styled(ContentContainer)`
  @media ${breakpointDesktopOnly} {
    display: none;
  }
`;

const CustomContentStickyElement = styled(ContentStickyElement)`
  background-color: ${compassColors.white};
  border-radius: 0;
  flex-direction: column;
  justify-content: start;
  margin: 80px 30px 0 80px;
  min-width: min(200px, 50%);
  height: 100%;
  @media ${breakpointWideDesktopOnly} {
    margin: 80px 30px 0 120px;
  }
`;

const ContentCard = styled.div`
  display: flex;
  flex-direction: column;
  @media ${breakpointDesktopOnly} {
    background-color: ${compassColors.offWhite};
    border-radius: 16px;
    margin: 100px 60px 0;
    min-width: 30vw;
  }
`;

const Image = styled("img", {
  shouldForwardProp: (prop) => prop !== "webpImage",
})<{ webpImage?: boolean }>`
  margin: 0 auto;
  margin-top: 20px;
  width: 100%;
  @media ${breakpointDesktopOnly} {
    max-height: 320px;
    ${({ webpImage }) =>
      webpImage &&
      css`
        margin-top: 0;
        width: 100%;
        max-width: 453.5px;
      `}
  }
`;

const WeightGrapImage = styled(Image)`
  margin-top: 0px;
`;

const TheScienceCTA = styled(Button)`
  margin-top: 18px;
  margin-bottom: 60px;
  @media ${breakpointDesktopOnly} {
    max-width: 340px;
  }
`;

const ImageContainer = styled.div`
  position: relative;
  text-align: center;
  background-color: ${compassColors.offWhite};
  border-radius: 15px;
  margin-top: 20px;
  padding-top: 17px;
  padding-bottom: 14px;
  @media ${breakpointDesktopOnly} {
    padding-top: 27px;
    padding-bottom: 24px;
    width: 70%;
    margin-left: auto;
    margin-right: auto;
  }
`;

const ImageText = styled.div`
  ${fontCaption}
  position: absolute;
  left: 50%;
  top: 50%;
  transform: translate(-50%, -50%);
  text-align: center;
  color: #191717;
  font-size: 0.6rem;
  line-height: 0.9rem;
  & span {
    font-size: 1.8rem;
    line-height: 2rem;
  }
  @media ${breakpointDesktopOnly} {
    font-size: min(1.5vw, 16px);
    line-height: min(1.7vw, 17px);
    & span {
      font-size: min(4.5vw, 46px);
      line-height: min(5vw, 54px);
    }
  }
`;

const CircleChartImage = styled(Image)`
  width: 48%;
  margin-top: 0;
  @media ${breakpointDesktopOnly} {
    width: 100%;
    max-width: 265px;
  }
`;

const DoctorsRecommendImage = styled(Image)`
  width: 86%;
  margin-bottom: 45px;
  @media ${breakpointDesktopOnly} {
    width: 100%;
    margin: 0;
    max-width: 350px;
  }
`;

const ImageContainerV2 = styled(ImageContainer)`
  @media ${breakpointDesktopOnly} {
    padding: 0;
    margin-top: 40px;
    margin-bottom: 20px;
    width: 80%;
  }
`;

const NonChartImageContainer = styled(ImageContainer)`
  padding-bottom: 0;
  padding-top: 0;
  & img {
    display: block;
  }
  @media ${breakpointDesktopOnly} {
    padding: 0;
    margin: 0 auto;
    width: 100%;
  }
`;

const NonChartImageText = styled.div`
  ${fontTitle}
  color: ${compassColors.blueberry};
  margin: 22px auto;
  font-weight: 700;
  width: 60%;
  font-size: 1rem;
  line-height: 1rem;
  letter-spacing: 0.08em;
  & span {
    font-size: 1.6rem;
    line-height: 1.9rem;
    letter-spacing: 0.03em;
  }
  @media ${breakpointDesktopOnly} {
    width: 100%;
    margin-top: 0;
    margin-bottom: 40px;
    font-size: min(1.2em, 1.6vw);
    line-height: min(1.2vw, 1rem);
    letter-spacing: 0.09em;
    & span {
      font-size: min(2.2vw, 1.7em);
      line-height: 2rem;
    }
  }
`;

const Container = styled.div`
  background-color: ${compassColors.white};
  border-radius: 0 80px 0 0;
`;

export default function TheScience({
  gotoNextPage,
}: ClinicalIntroContentProps) {
  const getScienceHeaderSection = () => {
    return (
      <>
        <ContentTitle css={{ marginTop: "60px", marginBottom: "14px" }}>
          {i18n.t("introClinical:science:header:title")}
        </ContentTitle>
        <ContentText>{i18n.t("introClinical:science:header:text")}</ContentText>
      </>
    );
  };

  const getScienceCards = () => {
    return (
      <>
        <ContentCard>
          <ImageContainer>
            <CircleChartImage src={BodyWeightLoss} alt="" />
            <ImageText>
              <Text
                i18nKeyOrText="t:introClinical:science:contentCard:circleChartImageText"
                components={{ span: <span /> }}
              />
            </ImageText>
          </ImageContainer>
          <ContentTextLarge>
            <Text i18nKeyOrText="t:introClinical:science:contentCard:text:general:text1" />
            <span>‡</span>
          </ContentTextLarge>
        </ContentCard>
        <ContentCard>
          <NonChartImageContainer>
            <WeightGrapImage webpImage src={WeightGraphMed} alt="" />
          </NonChartImageContainer>
          <ContentTextLarge>
            <Text i18nKeyOrText="t:introClinical:science:contentCard:text:general:text2" />
            <span>†</span>
          </ContentTextLarge>
        </ContentCard>
        <ContentCard>
          <ImageContainerV2>
            <NonChartImageText>
              <Text
                i18nKeyOrText="t:introClinical:science:contentCard:doctorsRecommendBarText"
                components={{ span: <span /> }}
              />
            </NonChartImageText>
            <DoctorsRecommendImage src={DoctorsRecommend} alt="" />
          </ImageContainerV2>
          <ContentTextLarge>
            <Text i18nKeyOrText="t:introClinical:science:contentCard:text:general:text3" />
            <span>
              <sup>1</sup>
            </span>
          </ContentTextLarge>
        </ContentCard>
      </>
    );
  };

  const getScienceCTA = () => {
    return (
      <TheScienceCTA
        borderRadius="32px"
        onClick={() => {
          trackBuyflowEvent("BuyflowButtonClicked", {
            buttonName: "theScience",
            pageId: "noomMedLP",
          });
          gotoNextPage();
        }}
      >
        {i18n.t("introClinical:science:cta")}
      </TheScienceCTA>
    );
  };

  return (
    <Container>
      <CustomContainer>
        {getScienceHeaderSection()}
        {getScienceCards()}
        {getScienceCTA()}
      </CustomContainer>
      <ContentInnerScrollContainer css={{ borderRadius: "0 80px 16px 0" }}>
        <CustomContentStickyElement>
          {getScienceHeaderSection()}
          {getScienceCTA()}
        </CustomContentStickyElement>
        <ScrollableContentContainer css={{ margin: "0 0 60px", width: "100%" }}>
          {getScienceCards()}
        </ScrollableContentContainer>
      </ContentInnerScrollContainer>
    </Container>
  );
}
